
/**
 * generation_metadata のパースユーティリティ
 *
 * 属性の項目名・値の日本語表示は prompt_attribute_nodes（DB）から取得した
 * 属性ツリーを使って動的に解決する。生成画面でユーザーが見ているラベルと
 * 完全に一致させるため、ハードコードの定数は hasGenerationInfo 判定用の
 * フォールバックとしてのみ使用する。
 */

// --- 型定義 ---

export interface GenerationAttributes {
  background?: string;
  scene?: string;
  seasons?: string;
  hand?: string;
  orientation?: string;
  effect?: string;
  aspect_ratio?: string;
  model_id?: string;
  actor_name?: string;
  person?: {
    none?: boolean;
    gender?: string;
    ethnicity?: string;
    age?: number;
    [key: string]: unknown;
  };
  gender?: string;
  ethnicity?: string;
  age?: number;
  main_copy?: string;
  sub_copy?: string;
  [key: string]: unknown;
}

export interface ReferenceImage {
  url: string;
  label?: string;
}

export interface AttributeItem {
  key: string;
  label: string;
  value: string;
  /** セクション番号（セクション境界で余白を入れるために使用） */
  section: number;
}

export type ComplianceSeverity = 'low' | 'mid' | 'high';

export interface AppliedComplianceRuleset {
  code: string;
  name: string;
  severity: ComplianceSeverity;
}

export interface ComplianceInfo {
  industry_categories: string[];
  applied_rulesets: AppliedComplianceRuleset[];
}

/** 音声素材 (BGM / ナレーション) の生成情報。generation_metadata のフラット形状から抽出する。 */
export interface AudioGenerationInfo {
  /** 生成プロバイダ ('elevenlabs' / 'lyria' / 'lyria-pro' / 'gemini') */
  provider: string | null;
  /** 生成モデル ID (`model`、無ければ旧レコードの `model_id`) */
  model: string | null;
  /** 音声種別。BGM は actual_duration_ms / song_title、ナレーションは narration_text / voice_id を持つ */
  kind: 'bgm' | 'narration';
  songTitle: string | null;
  /** 実尺 (ms)。ffprobe 実測値 */
  actualDurationMs: number | null;
  /** Lyria Pro の尺指示 (秒) */
  requestedDurationSeconds: number | null;
  isInstrumental: boolean | null;
  genre: string | null;
  mood: string | null;
  bpm: number | null;
  musicalKey: string | null;
}

export interface ParsedGenerationInfo {
  mode: 'attributes' | 'prompt' | null;
  attributes: GenerationAttributes;
  referenceImages: ReferenceImage[];
  /** 動画生成 (v2v) の参照動画 URL (reference_video_urls)。動画以外は空配列 */
  referenceVideos: string[];
  /** AI俳優の単数ミラー（= model_ids[0]）。後方互換用。正本は素材レコードの model_ids[]。 */
  modelId: string | null;
  /** AI俳優モデルID配列（複数俳優）。generation_metadata に配列があればそれ、無ければ
   * 単数ミラーを 1 要素配列に。情報なしは null。素材レコードの model_ids[] が真の正本。 */
  modelIds: string[] | null;
  actorName: string | null;
  /** シナリオフレーム生成時の「今回生成するシーン」セクション */
  scenePrompt: Array<{ key: string; value: string }> | null;
  /** 媒体要件（プラットフォーム / メニュー / 出力画像） */
  mediaSpecLabel: string | null;
  /** 生成モデル名 */
  generationModel: string | null;
  /** 生成所要秒数 */
  generationSeconds: number | null;
  /** OpenAI Images API が実際に使用した quality ('low'/'medium'/'high')。
   * quality='auto' 指定時に API が選んだ値を表示するために保持。gpt-image-2-auto 以外では通常 null。 */
  providerQuality: string | null;
  /** 動画生成メタデータかどうか */
  isVideo: boolean;
  /** 音声生成 (BGM / ナレーション) メタデータかどうか */
  isAudio: boolean;
  /** 音声生成の詳細 (isAudio のときのみ) */
  audioInfo: AudioGenerationInfo | null;
  /** 動画の長さ（秒） */
  videoDuration: number | null;
  /** 動画の解像度 */
  videoResolution: string | null;
  /** 動画の音声生成有無 */
  videoHasAudio: boolean | null;
  /** 動画生成プロンプト */
  videoPrompt: string | null;
  /** 商品を含めるか（動画生成時） */
  videoIncludeProduct: boolean | null;
  /** プロンプト入力モードで使用されたプロンプト */
  directPrompt: string | null;
  /** 適用されたコンプライアンスルール（薬機法・景表法等） */
  compliance: ComplianceInfo | null;
  /** ユーザーが選択した元素材のID一覧（AI俳優/ロゴ/ガイドラインは含まない）。
   * - `string[]`: 情報あり（空配列は「素材なしで生成した」）
   * - `null`: 情報なし（旧データ。復元時は手を付けない） */
  sourceMaterialIds: string[] | null;
  /** 復元用のトップレベル設定スナップショット（媒体要件ID・生成モデル等）。
   * キーが存在しない旧データは null。 */
  settingsSnapshot: Record<string, unknown> | null;
}

/** prompt_attribute_nodes から取得される属性ノード */
export interface AttributeNode {
  id: string;
  key: string;
  label: string;
  type: 'group' | 'option' | 'numeric';
  metadata?: Record<string, unknown> | null;
  sort_order?: number;
  prompt_text?: string | null;
  is_sensitive?: boolean;
  children?: AttributeNode[];
}

// --- フォールバック定数（hasGenerationInfo 判定用のみ） ---

const ATTRIBUTE_DEFAULTS: Record<string, unknown> = {
  background: 'none',
  scene: 'none',
  seasons: 'none',
  hand: 'none',
  orientation: 'none',
  effect: 'none',
  aspect_ratio: 'none',
};

// 属性ツリーに載らないキーの表示名マッピング。
// hand / orientation など属性ツリーに存在するキーは DB の label
// （OEM 組織は organization_attribute_overrides で上書き済み）を使うため
// ここでは扱わない。
const LABEL_OVERRIDES: Record<string, string> = {
  generate_text_from_ec: '商品情報からテキスト生成',
  disclaimer_text: '注意書き',
  aspect_ratio: 'アスペクト比',
  cta: 'CTA',
  main_copy: 'メインコピー',
  sub_copy: 'サブコピー',
  additional_prompt: '追加プロンプト',
  overall_impression: '雰囲気・印象',
  seasons: '季節・気候',
  other_instructions: 'プロンプト',
};

/** 値が長文テキストになる属性キー（改行表示用） */
export const TEXT_ATTRIBUTE_KEYS = new Set([
  'main_copy',
  'sub_copy',
  'cta',
  'disclaimer_text',
  'additional_prompt',
  'other_instructions',
]);

// --- 関数 ---

/** 参照画像ラベルの整形（新形式/旧形式対応）
 * 「素材画像」系のラベルは null を返す（表示不要） */
export function getReferenceLabel(label?: string): string | null {
  if (!label) return null;
  if (label.startsWith('画像')) {
    // 「画像[A:D]（素材画像3）：」→ 素材画像なのでラベル不要
    const match = label.match(/（(素材画像.+?)）/);
    if (match) return null;
    return label.replace(/:$/, '');
  }
  if (label.startsWith('【') && label.endsWith('】')) return label.slice(1, -1);
  return label;
}

/**
 * 属性ツリーから key → label のフラットマップを構築する。
 * children を再帰的に走査し、すべてのノードのラベルを収集する。
 */
function buildLabelMaps(tree: AttributeNode[]): {
  groupLabels: Map<string, string>;
  optionLabels: Map<string, Map<string, string>>;
  numericUnits: Map<string, string>;
  sortOrders: Map<string, number>;
} {
  const groupLabels = new Map<string, string>();
  const optionLabels = new Map<string, Map<string, string>>();
  const numericUnits = new Map<string, string>();
  const sortOrders = new Map<string, number>();

  function walkChildren(groupKey: string, children: AttributeNode[]) {
    for (const child of children) {
      if (child.type === 'option') {
        let map = optionLabels.get(groupKey);
        if (!map) {
          map = new Map();
          optionLabels.set(groupKey, map);
        }
        map.set(child.key, child.label);
      } else if (child.type === 'numeric') {
        const unit = (child.metadata as Record<string, unknown>)?.unit as string | undefined;
        if (unit) numericUnits.set(child.key, unit);
      } else if (child.type === 'group' && child.children) {
        // サブグループ: ラベルも登録し、子を再帰
        let map = optionLabels.get(groupKey);
        if (!map) {
          map = new Map();
          optionLabels.set(groupKey, map);
        }
        map.set(child.key, child.label);
        walkChildren(groupKey, child.children);
      }
    }
  }

  // GenerationWorkspace.tsx と同じ表示順
  const ROOT_ORDER = ['background', 'scene', 'seasons_weather', 'hand', 'orientation', 'effect', 'person'];
  const rootOrderMap = new Map(ROOT_ORDER.map((key, idx) => [key, idx]));

  // 属性ツリー外の属性（スタイル → テキスト → 媒体の順）
  const EXTRA_ORDER: [string, number][] = [
    ['overall_impression', 100],
    ['main_copy', 200],
    ['sub_copy', 201],
    ['generate_text_from_ec', 202],
    ['cta', 203],
    ['disclaimer_text', 204],
    ['additional_prompt', 205],
    ['other_instructions', 206],
    ['aspect_ratio', 300],
  ];
  for (const [key, order] of EXTRA_ORDER) {
    sortOrders.set(key, order);
  }

  // composition / style は画面グルーピング用の仮想 root。
  // その配下の group(background, scene, ...) を root と同等に扱うため平坦化する。
  const VIRTUAL_GROUP_KEYS = new Set(['composition', 'style']);
  const effectiveRoots: AttributeNode[] = tree.flatMap((n) =>
    VIRTUAL_GROUP_KEYS.has(n.key) ? (n.children ?? []) : [n]
  );

  for (const node of effectiveRoots) {
    const displayLabel = LABEL_OVERRIDES[node.key] || node.label;
    groupLabels.set(node.key, displayLabel);

    // ソート順を登録
    const rootIdx = rootOrderMap.has(node.key)
      ? rootOrderMap.get(node.key)!
      : ROOT_ORDER.length + (node.sort_order ?? 0);
    sortOrders.set(node.key, rootIdx);

    if (node.children) {
      walkChildren(node.key, node.children);

      // person グループの子要素もグループとして登録
      if (node.key === 'person') {
        for (let ci = 0; ci < node.children.length; ci++) {
          const child = node.children[ci];
          if (child.type === 'group' && child.children) {
            const childDisplayLabel = LABEL_OVERRIDES[child.key] || child.label;
            groupLabels.set(child.key, childDisplayLabel);
            walkChildren(child.key, child.children);
          }
          if (child.type === 'numeric') {
            const childDisplayLabel = LABEL_OVERRIDES[child.key] || child.label;
            groupLabels.set(child.key, childDisplayLabel);
            const unit = (child.metadata as Record<string, unknown>)?.unit as string | undefined;
            if (unit) numericUnits.set(child.key, unit);
          }
          // person 子属性のソート順（person_ プレフィックス付き）
          // personのROOT_ORDER位置（6）を基準に、子要素を 6.00, 6.01, ... と配置
          const personBaseOrder = rootOrderMap.get('person') ?? ROOT_ORDER.length;
          sortOrders.set(`person_${child.key}`, personBaseOrder + (child.sort_order ?? ci) * 0.01);
        }
      }
    }
  }

  return { groupLabels, optionLabels, numericUnits, sortOrders };
}

/**
 * 属性ツリーを使って、保存された属性値を日本語表示に変換する。
 * ツリーが渡されなければフォールバック（key をそのまま返す）。
 */
export function resolveAttributeLabels(
  attrs: GenerationAttributes | null,
  attributeTree: AttributeNode[] | undefined,
  options?: { excludeTextResponse?: boolean; productTextGenAsLabel?: boolean },
): AttributeItem[] {
  if (!attrs) return [];

  const result: AttributeItem[] = [];
  const hasActor = Boolean(attrs.model_id);

  // 属性ツリーからラベルマップを構築
  const { groupLabels, optionLabels, numericUnits, sortOrders } = attributeTree
    ? buildLabelMaps(attributeTree)
    : { groupLabels: new Map<string, string>(), optionLabels: new Map<string, Map<string, string>>(), numericUnits: new Map<string, string>(), sortOrders: new Map<string, number>() };

  // AI俳優名は個別表示するのでここでは追加しない（actor_name は GenerationInfoModal で直接表示）

  const personRelatedKeys = ['gender', 'ethnicity', 'age'];
  const skipKeys = new Set(['person', 'model_id', 'actor_name']);
  // exclude_text_response が ON だった生成は、メインコピー/CTA/注意書き等を
  // プロンプトに含めていないため AI 生成情報からも除外する (古い material の保険)。
  if (options?.excludeTextResponse) {
    skipKeys.add('main_copy');
    skipKeys.add('cta');
    skipKeys.add('disclaimer_text');
    skipKeys.add('generate_text_from_ec');
  }

  for (const [key, value] of Object.entries(attrs)) {
    if (skipKeys.has(key)) continue;
    if (value === undefined || value === null || value === '' || value === 'none' || value === false) continue;
    if (hasActor && personRelatedKeys.includes(key)) continue;
    if (attrs.person && personRelatedKeys.includes(key)) continue;

    // 値がオブジェクトの場合はスキップ（person など）
    if (typeof value === 'object') continue;
    // 人種=日本人はデフォルトのため非表示
    if (key === 'ethnicity' && value === 'jp') continue;

    const label = LABEL_OVERRIDES[key] || groupLabels.get(key) || key;
    let valueLabel: string;
    if (key === 'generate_text_from_ec' && value === 'never') {
      // 3値化で追加された「生成しない」(true/false は従来どおり下の boolean 分岐)
      valueLabel = '生成しない';
    } else if (typeof value === 'boolean' || value === 'true' || value === 'false') {
      const isOn = value === true || value === 'true';
      // studio 生成履歴では、商品情報からのテキスト生成が ON のとき
      // 「はい」ではなく「テキスト生成」と表示する（AI生成情報は従来通り「はい」）
      if (options?.productTextGenAsLabel && key === 'generate_text_from_ec' && isOn) {
        valueLabel = 'テキスト生成';
      } else {
        valueLabel = isOn ? 'はい' : 'いいえ';
      }
    } else {
      valueLabel = optionLabels.get(key)?.get(value as string) || String(value);
    }
    result.push({ key, label, value: valueLabel, section: 0 });
  }

  // person属性（AI俳優選択時はgender/ethnicity/ageをスキップ、それ以外は表示）
  const person = attrs.person;
  if (person && !person.none) {
    const personSkipKeys = hasActor ? ['gender', 'ethnicity', 'age'] : [];
    for (const [pKey, pValue] of Object.entries(person)) {
      if (pKey === 'none') continue;
      if (personSkipKeys.includes(pKey)) continue;
      if (pValue === undefined || pValue === null || pValue === '' || pValue === 'none') continue;
      // 人種=日本人はデフォルトのため非表示
      if (pKey === 'ethnicity' && pValue === 'jp') continue;

      const label = groupLabels.get(pKey) || pKey;

      if (typeof pValue === 'number') {
        // 数値属性（age など）
        const unit = numericUnits.get(pKey) || '';
        result.push({ key: `person_${pKey}`, label, value: `${pValue}${unit}`, section: 0 });
      } else if (typeof pValue === 'string') {
        const valueLabel = optionLabels.get(pKey)?.get(pValue) || pValue;
        result.push({ key: `person_${pKey}`, label, value: valueLabel, section: 0 });
      }
    }
  }

  // 生成画面と同じ表示順にソート
  const FALLBACK = 9999;
  result.sort((a, b) => {
    const aOrder = sortOrders.get(a.key) ?? FALLBACK;
    const bOrder = sortOrders.get(b.key) ?? FALLBACK;
    return aOrder - bOrder;
  });

  // ソート順の値からセクション番号を付与
  // 0–99: 背景・構図+人物, 100–199: スタイル, 200–299: テキスト, 300+: 媒体
  for (const item of result) {
    const order = sortOrders.get(item.key) ?? FALLBACK;
    if (order < 100) item.section = 0;
    else if (order < 200) item.section = 1;
    else if (order < 300) item.section = 2;
    else item.section = 3;
  }

  return result;
}

/** デフォルトと異なる属性があるかの簡易判定（hasGenerationInfo 用） */
function hasNonDefaultAttributes(attrs: GenerationAttributes | null): boolean {
  if (!attrs) return false;

  const skipKeys = ['person', 'model_id', 'actor_name'];
  for (const [key, value] of Object.entries(attrs)) {
    if (skipKeys.includes(key)) continue;
    if (value === undefined || value === null || value === '') continue;
    if (typeof value === 'object') continue;
    if (ATTRIBUTE_DEFAULTS[key] === value) continue;
    return true;
  }

  const person = attrs.person;
  if (person && !person.none) {
    for (const [pKey, pValue] of Object.entries(person)) {
      if (pKey === 'none') continue;
      if (pValue === undefined || pValue === null || pValue === '' || pValue === 'none') continue;
      return true;
    }
  }

  return false;
}

/** generation_metadata をパースして生成情報を抽出（新旧形式対応） */
export function parseGenerationMetadata(metadata: Record<string, unknown> | null | undefined): ParsedGenerationInfo | null {
  if (!metadata || typeof metadata !== 'object') return null;

  const appMeta = metadata._app as Record<string, unknown> | undefined;
  let mode: 'attributes' | 'prompt' | null = null;
  let attrs: GenerationAttributes = {};
  let modelId: string | null = null;
  let actorName: string | null = null;

  // generation_metadata に配列が載っていればそれを、無ければ単数ミラーを配列化する。
  // (正本は素材レコードの model_ids[]。ここは参照画像フィルタ等のための補助。)
  const readModelIds = (...srcs: (Record<string, unknown> | undefined)[]): string[] | null => {
    for (const src of srcs) {
      const arr = src?.model_ids;
      if (Array.isArray(arr)) {
        const ids = arr.filter((m): m is string => typeof m === 'string' && Boolean(m));
        if (ids.length > 0) return ids;
      }
    }
    return null;
  };
  const referenceImages: ReferenceImage[] = [];

  let directPrompt: string | null = null;

  if (appMeta && typeof appMeta === 'object') {
    // 新形式: _app 構造
    // AI俳優生成 (actor_person / actor_character_sheet) はプロンプト入力の一種として
    // 扱う (user_inputs のユーザー入力を directPrompt に表示するため)
    const rawMode = appMeta.mode as string;
    mode = rawMode === 'attributes' ? 'attributes'
      : rawMode === 'prompt' || rawMode === 'actor_person' || rawMode === 'actor_character_sheet' ? 'prompt'
      : null;
    attrs = (appMeta.attributes as GenerationAttributes) || {};
    modelId = (appMeta.model_id as string) || null;
    actorName = (attrs.actor_name as string) || null;

    // 参照画像: contents[0].parts 内の file_url（text パートは読まない — 内部テンプレ混入の可能性があるため）
    const contents = metadata.contents as Array<{ parts?: Array<Record<string, unknown>> }> | undefined;
    if (contents && contents[0]?.parts) {
      let currentLabel: string | undefined;
      for (const part of contents[0].parts) {
        if ('text' in part && typeof part.text === 'string') {
          if (part.text.startsWith('画像') || part.text.startsWith('【')) {
            currentLabel = part.text;
          }
        }
        if ('file_url' in part && typeof part.file_url === 'string') {
          referenceImages.push({ url: part.file_url, label: getReferenceLabel(currentLabel) ?? undefined });
          currentLabel = undefined;
        }
      }
    }
    // contents を持たない形式（ストーリーボード絵コンテ等）では生成元画像を別フィールドから読む。
    // 種別ラベル付きの reference_images ([{url,label}]) を優先し、無ければ旧データの
    // reference_image_urls (URL 配列) にフォールバック。contents から拾えている場合は重複させない。
    if (referenceImages.length === 0) {
      const labeledRefs = metadata.reference_images;
      if (Array.isArray(labeledRefs)) {
        for (const r of labeledRefs) {
          if (!r || typeof r !== 'object') continue;
          const url = (r as Record<string, unknown>).url;
          const label = (r as Record<string, unknown>).label;
          if (typeof url === 'string' && url.trim()) {
            referenceImages.push({ url: url.trim(), label: typeof label === 'string' && label ? label : undefined });
          }
        }
      } else {
        const refUrls = metadata.reference_image_urls;
        if (Array.isArray(refUrls)) {
          for (const url of refUrls) {
            if (typeof url === 'string' && url.trim()) {
              referenceImages.push({ url: url.trim() });
            }
          }
        }
      }
    }
    // プロンプトは user_inputs からのみ取得（内部テンプレ混入経路を排除）
    const userInputs = appMeta.user_inputs as Record<string, unknown> | undefined;
    if (userInputs && typeof userInputs === 'object') {
      if (mode === 'prompt') {
        const p = userInputs.prompt;
        directPrompt = typeof p === 'string' && p.trim() ? p : null;
        // キャラクターシート生成はプロンプト本体が内部テンプレのため、
        // ユーザー入力は追加指示のみ (prompt は無い)
        if (!directPrompt) {
          const ai = userInputs.additional_instruction;
          directPrompt = typeof ai === 'string' && ai.trim() ? ai : null;
        }
      } else if (mode === 'attributes') {
        const ai = userInputs.additional_instruction;
        directPrompt = typeof ai === 'string' && ai.trim() ? ai : null;
      }
    }
  } else if (typeof metadata.duration_seconds === 'number') {
    // 動画生成メタデータ形式
    // NOTE: metadata.prompt は内部テンプレ（TELOP指示・レギュレーション等）が混ざるため読まない。
    // プロンプト表示は user_inputs.prompt が存在する prompt モードのみ。
    const videoUserInputs = metadata.user_inputs as Record<string, unknown> | undefined;
    const videoMode = videoUserInputs && typeof videoUserInputs === 'object'
      ? (videoUserInputs.mode as string | undefined) ?? null
      : null;
    let videoDirectPrompt: string | null = null;
    if (videoMode === 'prompt' && videoUserInputs) {
      const p = videoUserInputs.prompt;
      videoDirectPrompt = typeof p === 'string' && p.trim() ? p : null;
    }

    // 参照動画: reference_video_urls (v2v / 動画編集の入力元)
    const videoRefVideos: string[] = [];
    const refVideoUrls = metadata.reference_video_urls;
    if (Array.isArray(refVideoUrls)) {
      for (const url of refVideoUrls) {
        if (typeof url === 'string' && url.trim()) {
          videoRefVideos.push(url.trim());
        }
      }
    }

    // 参照画像: first_frame_url + reference_image_urls
    const videoRefImages: ReferenceImage[] = [];
    const refUrls = metadata.reference_image_urls as string[] | undefined;
    const hasReferenceImages = refUrls && Array.isArray(refUrls) && refUrls.length > 0;
    const firstFrameUrl = metadata.first_frame_url as string | undefined;
    if (firstFrameUrl) {
      videoRefImages.push({ url: firstFrameUrl, label: hasReferenceImages ? undefined : '開始フレーム' });
    }
    const lastFrameUrl = metadata.last_frame_url as string | undefined;
    if (lastFrameUrl) {
      videoRefImages.push({ url: lastFrameUrl, label: '終了フレーム' });
    }
    if (hasReferenceImages) {
      for (const url of refUrls) {
        if (typeof url === 'string' && url.trim()) {
          videoRefImages.push({ url });
        }
      }
    }

    return {
      mode: null,
      attributes: {},
      referenceImages: videoRefImages,
      referenceVideos: videoRefVideos,
      modelId: (metadata.model_id as string) || null,
      modelIds: readModelIds(metadata) ?? ((metadata.model_id as string) ? [metadata.model_id as string] : null),
      actorName: null,
      scenePrompt: null,
      mediaSpecLabel: null,
      generationModel: (metadata.model as string) || null,
      generationSeconds: null,
      providerQuality: null,
      isVideo: true,
      isAudio: false,
      audioInfo: null,
      videoDuration: metadata.duration_seconds as number,
      videoResolution: (metadata.resolution as string) || null,
      videoHasAudio: typeof metadata.generate_audio === 'boolean' ? metadata.generate_audio as boolean : null,
      videoPrompt: videoDirectPrompt,
      videoIncludeProduct: typeof metadata.include_product === 'boolean' ? metadata.include_product as boolean : null,
      directPrompt: videoDirectPrompt,
      compliance: null,
      sourceMaterialIds: null,
      settingsSnapshot: null,
    };
  } else if (isAudioGenerationMetadata(metadata)) {
    // 音声生成 (BGM / ナレーション) 形式。bgm_generator / narration_generator が書く
    // フラット形状で、`provider` + 音声固有キー (actual_duration_ms / narration_text 等) を持つ。
    // 旧形式ブランチ (metadata.prompt) より前に判定しないと BGM の prompt が誤って
    // 画像の旧形式として解釈される。
    const audioInfo = parseAudioGenerationInfo(metadata);
    // BGM のプロンプトは UI の「BGM 生成プロンプト」欄でユーザーが見て編集する文言
    // (内部テンプレ混入なし) なのでそのまま表示する。ナレーションの原稿はプレビュー側で
    // 表示済みのためここでは出さない。
    const bgmPrompt = audioInfo.kind === 'bgm' && typeof metadata.prompt === 'string' && metadata.prompt.trim()
      ? metadata.prompt
      : null;
    return {
      mode: bgmPrompt ? 'prompt' : null,
      attributes: {},
      referenceImages: [],
      referenceVideos: [],
      modelId: null,
      modelIds: null,
      actorName: null,
      scenePrompt: null,
      mediaSpecLabel: null,
      generationModel: audioInfo.model,
      generationSeconds: null,
      providerQuality: null,
      isVideo: false,
      isAudio: true,
      audioInfo,
      videoDuration: null,
      videoResolution: null,
      videoHasAudio: null,
      videoPrompt: null,
      videoIncludeProduct: null,
      directPrompt: bgmPrompt,
      compliance: null,
      sourceMaterialIds: null,
      settingsSnapshot: null,
    };
  } else if (metadata.prompt || metadata.mode || metadata.attributes) {
    // 旧形式: トップレベル構造
    mode = (metadata.mode as string) === 'attributes' ? 'attributes'
      : (metadata.mode as string) === 'prompt' ? 'prompt'
      : null;
    attrs = (metadata.attributes as GenerationAttributes) || {};
    modelId = (metadata.model_id as string) || null;
    actorName = (attrs.actor_name as string) || null;

    // 参照画像: gemini_request or asset_urls
    const geminiReq = metadata.gemini_request as Record<string, unknown> | undefined;
    if (geminiReq) {
      const contents = geminiReq.contents as Array<{ parts?: Array<Record<string, unknown>> }> | undefined;
      if (contents && contents[0]?.parts) {
        let currentLabel: string | undefined;
        for (const part of contents[0].parts) {
          if ('text' in part && typeof part.text === 'string') {
            if (part.text.startsWith('画像') || part.text.startsWith('【')) {
              currentLabel = part.text;
            }
          }
          if ('inline_data' in part) {
            const inlineData = part.inline_data as Record<string, unknown>;
            const dataStr = (inlineData?.data as string) || '';
            const url = dataStr.startsWith('(URL: ') ? dataStr.replace('(URL: ', '').replace(/\)$/, '') : '';
            if (url) {
              referenceImages.push({ url, label: getReferenceLabel(currentLabel) ?? undefined });
              currentLabel = undefined;
            }
          }
        }
      }
    } else {
      const assetUrls = metadata.asset_urls as string[] | undefined;
      if (assetUrls) {
        for (const url of assetUrls) {
          referenceImages.push({ url });
        }
      }
    }
  } else {
    return null;
  }

  // model_id を attrs にも反映
  if (modelId && !attrs.model_id) {
    attrs = { ...attrs, model_id: modelId };
  }

  // アスペクト比をトップレベルから attrs に反映（シナリオモード等）
  if (!attrs.aspect_ratio && typeof metadata.aspect_ratio === 'string') {
    attrs = { ...attrs, aspect_ratio: metadata.aspect_ratio };
  }

  // 旧形式データはプロンプト由来の heuristic 抽出を行わない（内部テンプレ混入のため）
  const scenePrompt = null;

  // 媒体要件ラベル（プラットフォーム / メニュー / 出力画像を階層表示）
  const appMetaObj = (metadata._app as Record<string, unknown>) ?? metadata;
  const mediaLines: string[] = [];
  if (appMetaObj.media_platform_name) mediaLines.push(String(appMetaObj.media_platform_name));
  if (appMetaObj.media_menu_name) mediaLines.push(`› ${appMetaObj.media_menu_name}`);
  if (appMetaObj.media_spec_name) mediaLines.push(`\u2002› ${appMetaObj.media_spec_name}`);
  const mediaSpecLabel = mediaLines.length > 0 ? mediaLines.join('\n') : null;

  // 生成モデル・生成秒数
  const generationModel = (metadata.model as string) || null;
  const generationSeconds = typeof appMetaObj.generation_seconds === 'number'
    ? Math.round(appMetaObj.generation_seconds)
    : null;

  // OpenAI Images API レスポンスから quality を抽出 (auto 指定時の実値を表示するため)
  const providerResp = appMetaObj.provider_response;
  let providerQuality: string | null = null;
  if (providerResp && typeof providerResp === 'object' && !Array.isArray(providerResp)) {
    const q = (providerResp as Record<string, unknown>).quality;
    if (typeof q === 'string' && (q === 'low' || q === 'medium' || q === 'high')) {
      providerQuality = q;
    }
  }

  // 適用されたコンプライアンスルール
  const compliance = parseComplianceInfo(appMetaObj.compliance);

  // ユーザーが選択した元素材のID（復元用）。キー自体が存在しない旧データは null。
  const rawSourceIds = appMetaObj.source_material_ids;
  const sourceMaterialIds: string[] | null = Array.isArray(rawSourceIds)
    ? rawSourceIds.filter((v): v is string => typeof v === 'string' && v.length > 0)
    : null;

  // 復元用トップレベル設定スナップショット（キー欠落は旧データ = null）。
  const rawSnapshot = appMetaObj.settings_snapshot;
  const settingsSnapshot: Record<string, unknown> | null =
    rawSnapshot && typeof rawSnapshot === 'object' && !Array.isArray(rawSnapshot)
      ? (rawSnapshot as Record<string, unknown>)
      : null;

  const modelIds = readModelIds(appMeta, metadata) ?? (modelId ? [modelId] : null);
  return { mode, attributes: attrs, referenceImages, referenceVideos: [], modelId, modelIds, actorName, scenePrompt, mediaSpecLabel, generationModel, generationSeconds, providerQuality, isVideo: false, isAudio: false, audioInfo: null, videoDuration: null, videoResolution: null, videoHasAudio: null, videoPrompt: null, videoIncludeProduct: null, directPrompt, compliance, sourceMaterialIds, settingsSnapshot };
}

/**
 * 音声生成 (BGM / ナレーション) のフラット形状か。
 * `_app` 無し・`duration_seconds` 数値無し (動画) の上で、`provider` と音声固有キーの
 * いずれかを持つものを音声とみなす。アップロード BGM (provider 無し) は対象外。
 */
export function isAudioGenerationMetadata(metadata: Record<string, unknown> | null | undefined): boolean {
  if (!metadata || typeof metadata !== 'object') return false;
  if (metadata._app && typeof metadata._app === 'object') return false;
  if (typeof metadata.duration_seconds === 'number') return false;
  if (typeof metadata.provider !== 'string' || !metadata.provider) return false;
  return (
    typeof metadata.actual_duration_ms === 'number'
    || typeof metadata.music_length_ms === 'number'
    || typeof metadata.song_title === 'string'
    || typeof metadata.narration_text === 'string'
    || typeof metadata.voice_id === 'string'
  );
}

function parseAudioGenerationInfo(metadata: Record<string, unknown>): AudioGenerationInfo {
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const kind: AudioGenerationInfo['kind'] =
    typeof metadata.narration_text === 'string' || typeof metadata.voice_id === 'string' ? 'narration' : 'bgm';
  return {
    provider: str(metadata.provider),
    model: str(metadata.model) ?? str(metadata.model_id),
    kind,
    songTitle: str(metadata.song_title),
    actualDurationMs: num(metadata.actual_duration_ms) ?? num(metadata.music_length_ms),
    requestedDurationSeconds: num(metadata.requested_duration_seconds),
    isInstrumental: typeof metadata.is_instrumental === 'boolean' ? metadata.is_instrumental : null,
    genre: str(metadata.genre),
    mood: str(metadata.mood),
    bpm: num(metadata.bpm),
    musicalKey: str(metadata.musical_key),
  };
}

function parseComplianceInfo(raw: unknown): ComplianceInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const rawIndustries = Array.isArray(obj.industry_categories) ? obj.industry_categories : [];
  const industries = rawIndustries.filter((v): v is string => typeof v === 'string' && v.length > 0);

  const rawRules = Array.isArray(obj.applied_rulesets) ? obj.applied_rulesets : [];
  const rules: AppliedComplianceRuleset[] = [];
  for (const item of rawRules) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const code = typeof r.code === 'string' ? r.code : '';
    const name = typeof r.name === 'string' ? r.name : '';
    if (!code && !name) continue;
    const rawSev = r.severity;
    const severity: ComplianceSeverity =
      rawSev === 'high' || rawSev === 'low' || rawSev === 'mid' ? rawSev : 'mid';
    rules.push({ code, name: name || code, severity });
  }

  if (industries.length === 0 && rules.length === 0) return null;
  return { industry_categories: industries, applied_rulesets: rules };
}

/** generation_metadata に有効な生成情報が含まれているか判定 */
export function hasGenerationInfo(metadata: Record<string, unknown> | null | undefined): boolean {
  const parsed = parseGenerationMetadata(metadata);
  if (!parsed) return false;
  if (parsed.isVideo) return true;
  if (parsed.isAudio) return true;
  if (parsed.mode === 'prompt' && parsed.directPrompt) return true;
  return parsed.referenceImages.length > 0 || hasNonDefaultAttributes(parsed.attributes);
}
